import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import React, { FC, ReactNode, useCallback, useRef, useState } from 'react'
import styled from 'styled-components'

interface ResizableThreePanelLayoutProps {
  leftPanel: ReactNode
  centerPanel: ReactNode
  rightPanel: ReactNode
  defaultLeftWidth?: number
  defaultCenterWidth?: number
  minPanelWidth?: number
  collapsible?: boolean
}

const COLLAPSED_PANEL_WIDTH = 40
const RESIZE_HANDLE_WIDTH = 6

const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  background-color: ${({ theme }) => theme.forecast.background1};
  overflow: hidden;
  border-radius: 0.5rem;
`

const Panel = styled.div<{ $width: number; $minWidth: number }>`
  width: ${({ $width }) => $width}px;
  min-width: ${({ $minWidth }) => $minWidth}px;
  background-color: ${({ theme }) => theme.forecast.background1};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
`

const FlexPanel = styled.div<{ $width: number; $minWidth: number }>`
  width: ${({ $width }) => $width}px;
  min-width: ${({ $minWidth }) => $minWidth}px;
  background-color: ${({ theme }) => theme.forecast.background1};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
  padding: 1rem;
`

const ResizeHandle = styled.div<{ $disabled?: boolean }>`
  width: 6px;
  background-color: ${({ theme }) => theme.forecast.border2};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'col-resize')};
  position: relative;
  transition: background-color 0.2s ease;
  flex-shrink: 0;
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  opacity: ${({ $disabled }) => ($disabled ? 0 : 1)};

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 2px;
    height: 30px;
    background-color: ${({ theme }) => theme.forecast.secondaryText};
    border-radius: 1px;
    opacity: 0.5;
    transition: all 0.2s ease;
  }

  &:hover {
    background-color: ${({ theme }) => theme.forecast.primary}20;

    &::after {
      opacity: 1;
      background-color: ${({ theme }) => theme.forecast.primary};
    }
  }

  &:active {
    background-color: ${({ theme }) => theme.forecast.primary}30;

    &::after {
      opacity: 1;
      background-color: ${({ theme }) => theme.forecast.primary};
    }
  }
`

const CollapseButton = styled.button`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.forecast.border2};
  background-color: ${({ theme }) => theme.forecast.background2};
  color: ${({ theme }) => theme.forecast.defaultText};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: ${({ theme }) => theme.forecast.primary}20;
    border-color: ${({ theme }) => theme.forecast.primary};
    color: ${({ theme }) => theme.forecast.primary};
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const CollapsedPanel = styled.div`
  width: ${COLLAPSED_PANEL_WIDTH}px;
  background-color: ${({ theme }) => theme.forecast.background2};
  border-right: 1px solid ${({ theme }) => theme.forecast.border2};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
  transition: all 0.3s ease;
`

const ExpandButton = styled(CollapseButton)`
  position: static;
  transform: none;

  &:hover {
    transform: none;
  }

  &:active {
    transform: scale(0.95);
  }
`

type ResizeTarget = 'left' | 'center' | null

const useContainerWidth = (ref: React.RefObject<HTMLDivElement>) => {
  const [width, setWidth] = useState(0)

  React.useEffect(() => {
    if (!ref.current) return

    const updateWidth = () => {
      if (ref.current) {
        setWidth(ref.current.getBoundingClientRect().width)
      }
    }

    const resizeObserver = new ResizeObserver(updateWidth)
    resizeObserver.observe(ref.current)
    updateWidth()

    return () => resizeObserver.disconnect()
  }, [ref])

  return width
}

const calculateRightPanelWidth = (
  containerWidth: number,
  leftWidth: number,
  centerWidth: number,
  minWidth: number,
  isCollapsed: boolean
): number => {
  if (isCollapsed) {
    return containerWidth - COLLAPSED_PANEL_WIDTH - RESIZE_HANDLE_WIDTH
  }

  const totalHandleWidth = RESIZE_HANDLE_WIDTH * 2
  return Math.max(
    minWidth,
    containerWidth - leftWidth - centerWidth - totalHandleWidth
  )
}

const constrainResize = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, value))
}

export const ResizableThreePanelLayout: FC<ResizableThreePanelLayoutProps> = ({
  leftPanel,
  centerPanel,
  rightPanel,
  defaultLeftWidth = 300,
  defaultCenterWidth = 400,
  minPanelWidth = 250,
  collapsible = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const containerWidth = useContainerWidth(containerRef)

  const [leftPanelWidth, setLeftPanelWidth] = useState(defaultLeftWidth)
  const [centerPanelWidth, setCenterPanelWidth] = useState(defaultCenterWidth)
  const [isResizing, setIsResizing] = useState<ResizeTarget>(null)
  const [isCollapsed, setIsCollapsed] = useState(false)

  const [savedLeftWidth, setSavedLeftWidth] = useState(defaultLeftWidth)
  const [savedCenterWidth, setSavedCenterWidth] = useState(defaultCenterWidth)

  const rightPanelWidth = calculateRightPanelWidth(
    containerWidth,
    leftPanelWidth,
    centerPanelWidth,
    minPanelWidth,
    isCollapsed
  )

  const handleCollapse = useCallback(() => {
    if (isCollapsed) {
      setLeftPanelWidth(savedLeftWidth)
      setCenterPanelWidth(savedCenterWidth)
    } else {
      setSavedLeftWidth(leftPanelWidth)
      setSavedCenterWidth(centerPanelWidth)
    }
    setIsCollapsed(!isCollapsed)
  }, [
    isCollapsed,
    leftPanelWidth,
    centerPanelWidth,
    savedLeftWidth,
    savedCenterWidth,
  ])

  const handleMouseDown = useCallback((target: ResizeTarget) => {
    setIsResizing(target)
  }, [])

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) return

      const containerRect = containerRef.current.getBoundingClientRect()
      const mouseX = e.clientX - containerRect.left

      if (isResizing === 'left') {
        const maxWidth = containerRect.width * 0.5
        const newWidth = constrainResize(mouseX, minPanelWidth, maxWidth)
        setLeftPanelWidth(newWidth)
      } else if (isResizing === 'center') {
        const minX = leftPanelWidth + RESIZE_HANDLE_WIDTH + minPanelWidth
        const maxX = containerRect.width - minPanelWidth - RESIZE_HANDLE_WIDTH
        const constrainedX = constrainResize(mouseX, minX, maxX)
        const newWidth = constrainedX - leftPanelWidth - RESIZE_HANDLE_WIDTH

        const totalWidth =
          leftPanelWidth +
          RESIZE_HANDLE_WIDTH +
          newWidth +
          RESIZE_HANDLE_WIDTH +
          minPanelWidth

        if (totalWidth <= containerRect.width) {
          setCenterPanelWidth(newWidth)
        }
      }
    },
    [isResizing, leftPanelWidth, minPanelWidth]
  )

  const handleMouseUp = useCallback(() => {
    setIsResizing(null)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }, [])

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing, handleMouseMove, handleMouseUp])

  return (
    <Container ref={containerRef}>
      {isCollapsed && collapsible ? (
        <CollapsedPanel>
          <ExpandButton onClick={handleCollapse} title='Expand document viewer'>
            <ChevronRightIcon />
          </ExpandButton>
        </CollapsedPanel>
      ) : (
        <>
          <Panel $width={leftPanelWidth} $minWidth={minPanelWidth}>
            {leftPanel}
          </Panel>

          <ResizeHandle onMouseDown={() => handleMouseDown('left')} />

          <Panel $width={centerPanelWidth} $minWidth={minPanelWidth}>
            {centerPanel}
            {collapsible && (
              <CollapseButton
                onClick={handleCollapse}
                title='Collapse document viewer'>
                <ChevronLeftIcon />
              </CollapseButton>
            )}
          </Panel>

          <ResizeHandle onMouseDown={() => handleMouseDown('center')} />
        </>
      )}

      <FlexPanel $width={rightPanelWidth} $minWidth={minPanelWidth}>
        {rightPanel}
      </FlexPanel>
    </Container>
  )
}
